import { Engine } from '../core/Engine';
import { LevelUI } from './LevelUI';
import { EconomyUI } from './EconomyUI';
import { LoafUI } from './LoafUI';
import { GameScreenUI } from './GameScreenUI';
import { LevelManager } from '../game/LevelManager';
import { Config } from '../core/Config';
import { WeaponSystem } from '../game/weapons/WeaponSystem';

/**
 * Main UI manager that coordinates all game UI components
 */
export class GameUI {
  private engine: Engine;
  private levelUI: LevelUI | null = null;
  private economyUI: EconomyUI | null = null;
  private loafUI: LoafUI | null = null;
  private gameScreenUI: GameScreenUI | null = null;
  private isMobile: boolean = false;
  
  constructor(engine: Engine) {
    this.engine = engine;
    
    console.log('GameUI: Initializing UI components');
    
    // Detect if running on mobile device
    this.isMobile = this.detectMobileDevice();
    
    // Create Level UI if enabled
    if (Config.LEVEL_SYSTEM.ENABLE_UI) {
      const levelManager = LevelManager.getInstance();
      this.levelUI = new LevelUI(levelManager);
    }
    
    // Create Economy UI
    this.economyUI = new EconomyUI();
    
    // Create Loaf UI
    this.loafUI = new LoafUI();
    
    // Create Game Screen UI
    if (Config.GAME_SYSTEM.ENABLE_GAME_SCREENS) {
      this.gameScreenUI = new GameScreenUI(engine);
    }
    
    // Initialize mobile UI if on a mobile device
    if (this.isMobile) {
      this.initializeMobileUI();
    }
    
    console.log('GameUI: All UI components initialized');
  }
  
  /**
   * Detect if the game is running on a mobile device
   */
  private detectMobileDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for common mobile device patterns in user agent
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    
    // Also check for touch capabilities
    const hasTouchCapability = 'ontouchstart' in window || 
                               navigator.maxTouchPoints > 0 || 
                               (navigator as any).msMaxTouchPoints > 0;
    
    const result = isMobileDevice || hasTouchCapability;
    console.log(`GameUI: Device detected as ${result ? 'mobile' : 'desktop'}`);
    
    return result;
  }
  
  /**
   * Initialize UI components specific to mobile devices
   */
  private initializeMobileUI(): void {
    console.log('GameUI: Initializing mobile UI components');
    
    // Setup mobile-specific styles for existing UI components
    this.setupMobileStyles();
    
    // Tell the weapon system to set up mobile controls
    const weaponSystem = this.engine.getWeaponSystem();
    if (weaponSystem) {
      weaponSystem.setupMobileControls();
    }
  }
  
  /**
   * Set up mobile-specific styles for UI components
   */
  private setupMobileStyles(): void {
    // Add a CSS class to the body to apply mobile styles
    document.body.classList.add('mobile-device');
    
    // Create and append a style element with mobile-specific CSS
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Mobile-specific styles */
      .mobile-device button {
        font-size: 12px !important;
        padding: 5px 10px !important;
      }
      
      /* Simplified weapon UI for mobile */
      .mobile-device #weapon-ui {
        transform: none !important;
        position: absolute !important;
        top: auto !important;
        bottom: 140px !important;
        right: 10px !important;
        left: auto !important;
        flex-direction: row !important;
        gap: 8px !important;
        padding: 6px 10px !important;
        background-color: rgba(0, 0, 0, 0.7) !important;
        border-radius: 20px !important;
        z-index: 100 !important;
      }
      
      .mobile-device #weapon-ui button {
        font-size: 14px !important;
        padding: 6px 12px !important;
        border-radius: 15px !important;
        min-width: auto !important;
        width: auto !important;
        font-weight: bold !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3) !important;
      }
      
      .mobile-device #crosshair {
        transform: translate(-50%, -50%) scale(0.7) !important;
      }
      
      /* Make level UI smaller on mobile */
      .mobile-device #level-ui {
        font-size: 14px !important;
        padding: 6px 12px !important;
        min-width: 160px !important;
        top: 5px !important;
      }
      
      /* Make economy UI smaller on mobile */
      .mobile-device #economy-ui {
        font-size: 14px !important;
        padding: 6px 10px !important;
        top: 5px !important;
        right: 5px !important;
      }
      
      /* Make loaf UI smaller on mobile */
      .mobile-device #loaf-ui {
        font-size: 14px !important;
        padding: 6px 10px !important;
        top: 5px !important;
        left: 5px !important;
      }
      
      /* Adjust debug panel on mobile */
      .mobile-device #debug-panel {
        font-size: 10px !important;
        padding: 4px !important;
      }
      
      /* Game screens adjustments for mobile */
      .mobile-device #start-screen,
      .mobile-device #game-over-screen,
      .mobile-device #game-win-screen {
        padding: 20px !important;
        max-width: 90% !important;
        font-size: 14px !important;
      }
      
      .mobile-device #start-screen h1,
      .mobile-device #game-over-screen h1,
      .mobile-device #game-win-screen h1 {
        font-size: 24px !important;
        margin: 0 0 15px 0 !important;
      }
      
      .mobile-device #start-screen p,
      .mobile-device #game-over-screen p,
      .mobile-device #game-win-screen p {
        font-size: 14px !important;
        margin-bottom: 20px !important;
        line-height: 1.4 !important;
      }
      
      .mobile-device #start-screen button,
      .mobile-device #game-over-screen button,
      .mobile-device #game-win-screen button {
        font-size: 16px !important;
        padding: 10px 20px !important;
      }
      
      /* Hide any desktop-only elements */
      .desktop-only {
        display: none !important;
      }
    `;
    
    document.head.appendChild(styleElement);
  }
  
  /**
   * Update all UI components
   */
  public update(): void {
    // Update Level UI
    if (this.levelUI) {
      this.levelUI.update();
    }
    
    // Update Economy UI
    if (this.economyUI) {
      this.economyUI.update();
    }
    
    // Update Loaf UI
    if (this.loafUI) {
      this.loafUI.update();
    }
    
    // Game Screen UI doesn't need regular updates
  }
  
  /**
   * Check if the game is running on a mobile device
   */
  public isMobileDevice(): boolean {
    return this.isMobile;
  }
} 